import { useEffect, useRef, useState } from 'react'
import styles from './ConversationId.module.css'

export type ConversationIdProps = {
  id: string | null
}

const COPIED_NOTICE_MS = 2000

/**
 * Tester-support affordance (VC-755): the full conversation UUID rendered as
 * unobtrusive muted text under the chat input, so issue screenshots identify
 * which conversation they came from. Clicking copies the id; the full UUID
 * stays visible either way so a screenshot alone is enough.
 *
 * Before the first send no id exists yet — the line still renders (as a
 * blank placeholder) so the composer doesn't jump when the id arrives.
 */
export default function ConversationId({ id }: ConversationIdProps) {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    },
    [],
  )

  if (id === null) {
    // Non-breaking space: plain whitespace would collapse in the flex
    // container and the reserved line would have zero height.
    return (
      <p className={styles.container} aria-hidden="true">
        {'\u00A0'}
      </p>
    )
  }

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(id)
    } catch {
      // Clipboard unavailable (insecure context, denied permission) — the id
      // is still selectable text, so silently skip the convenience copy.
      return
    }
    setCopied(true)
    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => setCopied(false), COPIED_NOTICE_MS)
  }

  return (
    <p className={styles.container}>
      <span>Samtale-ID:</span>
      <button
        type="button"
        className={styles.idButton}
        onClick={() => void handleCopy()}
        title="Klikk for å kopiere"
      >
        <code className={styles.id}>{id}</code>
      </button>
      <span role="status" className={styles.copied}>
        {copied ? 'Kopiert' : ''}
      </span>
    </p>
  )
}
